import { InteractionEvent } from '@logto/schemas';
import { useCallback, useMemo } from 'react';

import { bindSocialRelatedUser } from '@/apis/experience';
import useAccountSetupIncompleteErrorHandler from '@/hooks/use-account-setup-incomplete-error-handler';
import useApi from '@/hooks/use-api';
import useErrorHandler from '@/hooks/use-error-handler';
import useGlobalRedirectTo from '@/hooks/use-global-redirect-to';
import useSubmitInteractionErrorHandler from '@/hooks/use-submit-interaction-error-handler';

const useBindSocialRelatedUser = () => {
  const handleError = useErrorHandler();
  const preSignInErrorHandler = useSubmitInteractionErrorHandler(InteractionEvent.SignIn);
  const accountSetupIncompleteErrorHandler = useAccountSetupIncompleteErrorHandler();
  const errorHandlers = useMemo(
    () => ({ ...preSignInErrorHandler, ...accountSetupIncompleteErrorHandler }),
    [preSignInErrorHandler, accountSetupIncompleteErrorHandler]
  );
  const redirectTo = useGlobalRedirectTo();

  const asyncBindSocialRelatedUser = useApi(bindSocialRelatedUser);

  return useCallback(
    async (...payload: Parameters<typeof bindSocialRelatedUser>) => {
      const [error, result] = await asyncBindSocialRelatedUser(...payload);

      if (error) {
        await handleError(error, errorHandlers);

        return;
      }

      if (result?.redirectTo) {
        await redirectTo(result.redirectTo);
      }
    },
    [asyncBindSocialRelatedUser, handleError, errorHandlers, redirectTo]
  );
};

export default useBindSocialRelatedUser;
